import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "next", "prev", "dot"]
  static values = {
    auto: { type: Boolean, default: false },
    interval: { type: Number, default: 5000 }
  }

  connect() {
    this.updateControls()
    if (this.autoValue) {
      this.startAutoPlay()
    }
    
    // Create an Intersection Observer to update dots based on scroll position
    this.observer = new IntersectionObserver(this.onIntersection.bind(this), {
      root: this.containerTarget,
      threshold: 0.6
    })
    
    this.containerTarget.children.forEach((child, index) => {
      child.dataset.index = index
      this.observer.observe(child)
    })
  }

  disconnect() {
    this.stopAutoPlay()
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  next() {
    const width = this.containerTarget.offsetWidth
    this.containerTarget.scrollBy({ left: width, behavior: "smooth" })
    this.updateControls()
  }

  prev() {
    const width = this.containerTarget.offsetWidth
    this.containerTarget.scrollBy({ left: -width, behavior: "smooth" })
    this.updateControls()
  }

  scrollTo(event) {
    const index = event.currentTarget.dataset.index
    const child = this.containerTarget.children[index]
    if (child) {
      child.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" })
    }
  }

  onIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const index = entry.target.dataset.index
        this.updateDots(index)
      }
    })
    this.updateControls()
  }

  updateDots(activeIndex) {
    if (!this.hasDotTarget) return
    
    this.dotTargets.forEach((dot, index) => {
      if (index == activeIndex) {
        dot.classList.add("bg-primary")
        dot.classList.remove("bg-gray-300")
      } else {
        dot.classList.add("bg-gray-300")
        dot.classList.remove("bg-primary")
      }
    })
  }

  updateControls() {
    if (!this.hasPrevTarget || !this.hasNextTarget) return
    
    const scrollLeft = this.containerTarget.scrollLeft
    const maxScroll = this.containerTarget.scrollWidth - this.containerTarget.clientWidth
    
    // We use a small threshold for precision
    this.prevTarget.disabled = scrollLeft <= 5
    this.nextTarget.disabled = scrollLeft >= maxScroll - 5
    
    this.prevTarget.classList.toggle("opacity-50", this.prevTarget.disabled)
    this.nextTarget.classList.toggle("opacity-50", this.nextTarget.disabled)
  }

  startAutoPlay() {
    this.autoPlayInterval = setInterval(() => {
      const maxScroll = this.containerTarget.scrollWidth - this.containerTarget.clientWidth
      if (this.containerTarget.scrollLeft >= maxScroll - 5) {
        this.containerTarget.scrollTo({ left: 0, behavior: "smooth" })
      } else {
        this.next()
      }
    }, this.intervalValue)
  }

  stopAutoPlay() {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval)
    }
  }

  pauseAutoPlay() {
    this.stopAutoPlay()
  }

  resumeAutoPlay() {
    if (this.autoValue) {
      this.startAutoPlay()
    }
  }
}
